<template>
  <div class="organization-layout">
    <div class="organization-page-title-container organization-page-title-row">
      <PageTitle class="organization-page-title-row__title" title="Organization" />
      <HelpMenu />
    </div>
    <OrganizationHorizontalMenu
      :menuItems="menuItems"
      :initialTab="currentTab"
      :count_of_active_employees="sidebarCountStore.employees"
      :count_of_active_positions="sidebarCountStore.positions"
      :count_of_active_roles="readinessProductEnabled ? sidebarCountStore.roles : 0"
      :count_of_active_responsibilities="
        readinessProductEnabled ? sidebarCountStore.responsibilities : 0
      "
      :count_of_active_procedures="0"
      :count_of_active_departments="sidebarCountStore.departments"
      :count_of_active_job_categories="jobCategoriesCount"
      :count_of_active_job_families="jobFamiliesCount"
      @tab-selected="handleTabChange"
    />
    <div class="layout-content">
      <!-- Key on path only. Query changes (roleTab / resTab / positionTab) must not
           remount PositionForm — that resets the attached-roles pane back to empty/grid. -->
      <router-view :key="$route.path" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import OrganizationHorizontalMenu from "@components/OrganizationHorizontalMenu/OrganizationHorizontalMenu.vue";
import PageTitle from "@components/common/PageTitle.vue";
import HelpMenu from "@components/HelpMenu/HelpMenu.vue";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { resolveProductReadinessEnabled } from "@/utils/accountProductFlags";
import Home from "@components/assets/icons/Home.svg";
import Employee from "@components/assets/icons/Employee.svg";
import Position from "@components/assets/icons/Position.svg";
import Role from "@components/assets/icons/Role.svg";
import Responsibility from "@components/assets/icons/Responsibility.svg";
import Department from "@components/assets/department-icon.svg";
import Category from "@components/assets/category-icon.svg";
import JobFamilyIcon from "@components/assets/job-family/layers-three-01.svg";
import { getAccountData } from "@/api";
import { getListOfJobCategories } from "@/api/jobCategoryAPI";
import { getListOfJobFamilies } from "@/api/jobFamilyAPI";
import { LD_FLAG_IDEA_MDP_566, LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import {
  buildOrganizationMenuItems,
  organizationTabSlug,
} from "@components/OrganizationPage/organizationMenuItems";

const instance = getCurrentInstance();
const { proxy } = instance || {};
const router = instance?.proxy?.$router || useRouter();
const route = useRoute();
const sidebarCountStore = useSidebarCountStore();

const readinessProductEnabled = computed(() =>
  resolveProductReadinessEnabled(proxy?.$root?.currentAccountDetails)
);

const ideaMdp440Enabled = ref(false);
const ideaMdp566Enabled = ref(false);
const ideaMdp581Enabled = ref(false);
const jobCategoriesOn = ref(false);
const jobFamiliesOn = ref(false);
const jobCategoriesCount = ref(0);
const jobFamiliesCount = ref(0);

const loadJobConfiguration = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getAccountData(accountId);
    jobCategoriesOn.value = data?.job_categories_on === true;
    jobFamiliesOn.value = data?.job_families_on === true;
  } catch (error) {
    console.error("Error loading job configuration:", error);
    jobCategoriesOn.value = false;
    jobFamiliesOn.value = false;
  }
};

const loadJobCategoriesCount = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getListOfJobCategories(
      accountId,
      { archived: false },
      { page: 1, pageSize: 1 }
    );
    jobCategoriesCount.value = Number(data && data.count) || 0;
  } catch (error) {
    console.error("Error loading job category count:", error);
    jobCategoriesCount.value = 0;
  }
};

const loadJobFamiliesCount = async () => {
  const accountId = localStorage.getItem("currentAccountID");
  if (!accountId) return;
  try {
    const { data } = await getListOfJobFamilies(accountId, {}, { page: 1, pageSize: 1 });
    jobFamiliesCount.value = Number(data && data.count) || 0;
  } catch (error) {
    console.error("Error loading job family count:", error);
    jobFamiliesCount.value = 0;
  }
};

onBeforeMount(async () => {
  ideaMdp440Enabled.value = await proxy?.$getFlag?.("idea-MDP-440", false);

  const [ldFlag566, ldFlag581] = await Promise.all([
    proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, false),
    proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, false),
  ]);

  await loadJobConfiguration();

  ideaMdp566Enabled.value = ldFlag566 && jobCategoriesOn.value;
  ideaMdp581Enabled.value = ldFlag581 && jobFamiliesOn.value;

  if (ideaMdp566Enabled.value) {
    await loadJobCategoriesCount();
  }
  if (ideaMdp581Enabled.value) {
    await loadJobFamiliesCount();
  }
});

const currentTab = computed(() => {
  const path = router?.currentRoute?.path || "";
  const routeName = router?.currentRoute?.name || "";

  // Check for position detail routes (including nested role and responsibility)
  if (
    path.includes("/positions") ||
    routeName === "PositionDetail" ||
    routeName === "PositionDetailChild" ||
    routeName === "RoleDetailFromPosition" ||
    routeName === "ResponsibilityDetailFromPositionRole" ||
    routeName === "ResponsibilityDetailFromPositionRoleChild"
  ) {
    return "Positions";
  }

  // Check for role detail routes (from roles list)
  if (
    routeName === "ResponsibilityDetailFromRole" ||
    routeName === "ResponsibilityDetailFromRoleChild"
  ) {
    return "Roles"; // Responsibility accessed from role (not from position)
  }
  if (path.includes("/roles") || routeName === "RoleDetail" || routeName === "RoleDetailChild") {
    return "Roles"; // Role accessed from roles list
  }

  if (
    path.includes("/responsibilities") ||
    routeName === "ResponsibilityDetail" ||
    routeName === "ResponsibilityDetailChild"
  )
    return "Responsibilities";
  if (path.includes("/employees")) return "Employees";
  if (path.includes("/departments")) return "Departments";
  return "Dashboard";
});

const menuItems = computed(() =>
  buildOrganizationMenuItems({
    ideaMdp440Enabled: ideaMdp440Enabled.value,
    ideaMdp566Enabled: ideaMdp566Enabled.value,
    ideaMdp581Enabled: ideaMdp581Enabled.value,
    readinessProductEnabled: readinessProductEnabled.value,
    icons: {
      home: Home,
      employee: Employee,
      position: Position,
      role: Role,
      responsibility: Responsibility,
      department: Department,
      category: Category,
      jobFamily: JobFamilyIcon,
    },
  })
);

const handleTabChange = (tabLabel) => {
  if (!router) return;

  const targetTab = organizationTabSlug(tabLabel);
  const resolved = router.resolve({
    name: "NewOrganization",
    params: { tab: targetTab },
  });
  if (resolved.route.fullPath !== route.fullPath) {
    router.push({ name: "NewOrganization", params: { tab: targetTab } }).catch(() => {});
  }
};
</script>

<style scoped>
.organization-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
}

.layout-content {
  flex: 1;
  width: 100%;
  margin-top: 12px;
}

.organization-page-title-container {
  padding: 24px 0px 0px 0px;
}

.organization-page-title-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

.organization-page-title-row__title {
  flex: 1;
  min-width: 0;
}
</style>
